import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icon/Icon';
import { useDispatch, useSelector } from 'react-redux';
import {
	resetPage,
	setAllModels,
	setSortBy,
	setStories,
} from '@/stores/slices/modelsState';
import useLadyService from '@/services/LadyService';
import './select.scss';
import {
	getUserCurrentCity,
	getUserLang,
	setCity,
	setCurrentCity,
	setLang,
} from '@/stores/slices/userSlice';
import { showFilter } from '@/stores/slices/showFilterSlice';
import { clearTitle } from '@/stores/slices/titleSlice';
import { useTranslation } from 'react-i18next';
import useCityChange from '../../../hooks/useCityChange';
import useLanguageChange from '../../../hooks/useLanguageChange';
import Input from '../input/Input';
import { useFilterLogicContext } from '@/components/filter/FilterLogicProvider';

const Select = ({ ...props }) => {
	const {
		type = '',
		arrowSize,
		clazz,
		clazzText,
		clazzSvg,
		clazzWrapper,
		options,
		optionsDefault,
		name,
		size = 'l',
		subTitle = '',
		setValue = undefined,
		isSearch = false,
		searchPlaceHolder = '',
		postApi = false,
		onChange = false,
		paramsOnChange = false,
		defaultTitle = '',
		withoutIcon = false,
		hideFlag = false,
		withoutTitle = false,
		placeholder,
		disabled,
		required,
		prefix = '',
		withIcon = false,
		controlledValue = undefined,
		controlledTitle = undefined,
		controlledIcon = undefined,
		itemClazzWrapper = '',
		iconLeft = false,
		bodyClazz = '',
	} = props;

	const dispatch = useDispatch();
	const { t } = useLadyService();
	const FILTER = useFilterLogicContext();

	const { i18n } = useTranslation();
	const filterOpen = useSelector(showFilter);
	const currentCity = useSelector(getUserCurrentCity);
	const lang = useSelector(getUserLang);
	const changeCity = useCityChange();
	const changeLanguage = useLanguageChange();

	const HandleTitle = () => {
		if (withoutTitle || defaultTitle) {
			if (name === 'agencyCardsPerPage') {
				return defaultTitle + ' ' + t('profiles');
			} else {
				return defaultTitle;
			}
		} else if (!!options?.length) {
			return options[0].title;
		} else {
			return '';
		}
	};

	const [title, setTitle] = useState(HandleTitle());

	useEffect(() => {
		if (!!options.length && name === 'lang-ai') {
			setTitle(options[0].title);
		} else if (name === 'agencyCardsPerPage') {
			setTitle(defaultTitle + ' ' + t('profiles'));
		}
	}, [defaultTitle, t, options, name]);

	const [searchVal, setSearchVal] = useState('');

	const [isOpen, setIsOpen] = useState(false);

	const selectRef = useRef(null);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		if (selectRef.current && !selectRef.current.contains(event.target)) {
			setIsOpen(false);
			event.stopPropagation();
		}
	};

	const iconNat = !!options?.length
		? options?.filter((i) => {
				return i.name === defaultTitle;
			})
		: '';

	const [mainIcon, setMainIcon] = useState(
		!withoutIcon && !!options?.length
			? !!defaultTitle &&
				name === 'create-nationalist' &&
				!!iconNat?.length
				? iconNat[0].icon
				: options[0].icon
			: null,
	);

	useEffect(() => {
		if (controlledValue !== undefined) {
			return;
		}

		if (
			name !== 'city-create' &&
			name !== 'visuals_select' &&
			!postApi &&
			name !== 'agencyCardsPerPage'
		) {
			setTitle(options[0].title);
		}

		if (name === 'city') {
			if (currentCity) {
				setTitle(currentCity.title);
				setMainIcon(currentCity.icon);
			}
		}
		if (name === 'language') {
			setTitle(lang.toUpperCase());
		}
	}, [currentCity, name, lang, options, postApi]);

	useEffect(() => {
		if (name === 'city') {
			setTitle(currentCity.title);
			setMainIcon(currentCity.icon);
		}
	}, [filterOpen]);

	function setValues(i, paramsOnChange) {
		setTitle(i.title || i.name);
		setMainIcon(i.icon);
		!!setValue && setValue(i.title);
		if (onChange) {
			if (paramsOnChange) {
				onChange(i, paramsOnChange);
			} else if (name === 'districts') {
				onChange(i);
			} else {
				onChange(i.value ? i.value : i.name ? i.name : i);
			}
		}
	}

	const handleClick = (i) => {
		setValues(i, paramsOnChange);
		if (name === 'language') {
			changeLanguage(i.value);
			i18n.changeLanguage(i.value);
			dispatch(setLang(i.value));
		}
		if (name === 'sort') {
			dispatch(setSortBy(i.value));
		}
		if (name === 'agencyCardsPerPage') {
			setTitle(i.title + ' ' + t('profiles'));
			setValues(
				{ title: i.title + ' ' + t('profiles'), value: i.value },
				paramsOnChange,
			);
		}
		if (name === 'city') {
			dispatch(resetPage());
			dispatch(setAllModels([]));
			dispatch(setStories([]));
			changeCity(i.slug);
			// change for district handling
			dispatch(
				setCurrentCity({
					id: i.id,
					title: i.title,
					icon: i.icon,
					highlight: i.highlight,
					slug: i.slug,
				}),
			);

			dispatch(setCity(i.slug));
			if (!filterOpen) {
				dispatch(setAllModels([]));
			}
			dispatch(clearTitle());
			FILTER.resetFilter(false);
		}

		setIsOpen(false);
	};

	const handleOpenSearch = (e) => {
		setIsOpen((prev) => !prev);
		const container = e.currentTarget.parentNode; // or specific selector if needed
		const searchInput = container.querySelector("input[type='search']");

		setTimeout(() => {
			if (searchInput) {
				searchInput.focus();
			} else {
				console.info('No input present.');
			}
		}, 100);
	};

	const handleTitledIcon = () => {
		if (!!options?.length) {
			return options[0]?.tippy;
		}
	};

	const searchedData =
		searchVal && !!options?.length
			? options?.filter((item) => {
					return item.title
						?.toLowerCase()
						.includes(searchVal.toLowerCase());
				})
			: options;

	// Handle controlled value from outside
	useEffect(() => {
		if (controlledValue !== undefined && controlledTitle !== undefined) {
			setTitle(controlledTitle);

			if (controlledIcon !== null || controlledIcon !== undefined) {
				setMainIcon(controlledIcon);
			}
		}
	}, [controlledValue, controlledTitle, controlledIcon]);

	const Wrapper = ({ children }) =>
		isSearch ? (
			<div className="dropdown__search-select">{children}</div>
		) : (
			children
		);

	return (
		<div
			className={`select${required ? ' required' : ''}${
				isOpen ? ' select_active' : ''
			}${clazzWrapper ? ` ${clazzWrapper}` : ''}`}
			ref={selectRef}
		>
			<button
				type={'button'}
				disabled={disabled}
				className={`select__button${clazz ? ` ${clazz}` : ''}${
					size ? ` size-${size}` : ''
				}${disabled ? ' __disabled' : ''}`}
				onClick={handleOpenSearch}
			>
				{withIcon && (
					<Icon
						size={'m'}
						spritePath={
							mainIcon ? mainIcon : name === 'sort' ? 'sort' : ''
						}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				)}

				{placeholder ? (
					title !== '' ? (
						<span className={'select__subtitle'}>
							{placeholder}
						</span>
					) : (
						subTitle && (
							<span className="select__placeholder">
								{subTitle}
							</span>
						)
					)
				) : null}

				{placeholder ? (
					title === '' || title === undefined ? (
						<span className={'select__placeholder'}>
							{placeholder}
						</span>
					) : (
						<span className={'select__value'}>
							{`${prefix !== '' && title ? `${prefix} ` : ''}${title ? title : ''}`}
						</span>
					)
				) : (
					<span className={'select__value'}>
						{`${prefix !== '' && title ? `${prefix} ` : ''}${title ? title : ''}`}
					</span>
				)}

				{name === 'paymentsMethods' ? (
					<Icon
						type={'payment'}
						size={'m'}
						spritePath={mainIcon}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{name === 'sort' && !withIcon ? (
					<Icon
						size={'m'}
						spritePath={'sort'}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{(name === 'city' ||
					name === 'city-create' ||
					type === 'flag') &&
				(!withoutIcon || !hideFlag) ? (
					<Icon
						title={handleTitledIcon()}
						type={'flag'}
						spritePath={mainIcon ? mainIcon : 'EU'}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{arrowSize ? (
					<Icon
						clazz={'select__arrow'}
						size={arrowSize}
						spritePath={'chevrone-down'}
					/>
				) : null}
			</button>

			<div
				className={`select__body${optionsDefault ? ` gap-8` : ''} ${bodyClazz}`}
			>
				{isSearch && (
					<label
						htmlFor={`${name}-search`}
						className="select__search"
					>
						<Input
							type="search"
							placeholder={searchPlaceHolder}
							id={`${name}-search`}
							clazz={'search__input'}
							name={`${name}-search`}
							value={searchVal}
							setValue={setSearchVal}
						/>
					</label>
				)}

				{optionsDefault && (
					<div className={'d-flex justify-sb gap-8'}>
						{optionsDefault.map((i, index) => {
							return (
								<button
									type={'button'}
									key={index + 1}
									onClick={() => handleClick(i)}
									onKeyUp={(e) => {
										if (e.key === 'Enter') handleClick(i);
									}}
									className={`select__item justify-center button--tetriary`}
								>
									{i.icon ? (
										<Icon
											type={'flag'}
											spritePath={`${i.icon}`}
											title={i?.tippy}
										/>
									) : (
										''
									)}
									{clazzText ? (
										<span className={clazzText}>
											{i.icon}
										</span>
									) : (
										i.icon
									)}
								</button>
							);
						})}
					</div>
				)}

				<Wrapper>
					{!!options?.length &&
						searchedData.map((i, index) => {
							return (
								<button
									type={'button'}
									key={index + 1}
									onClick={() => handleClick(i)}
									onKeyUp={(e) => {
										if (e.key === 'Enter') handleClick(i);
									}}
									disabled={i.disabled}
									className={`select__item ${itemClazzWrapper} ${
										i.disabled ? '_disabled' : ''
									}`}
								>
									{i.icon ? (
										<Icon
											type={withIcon ? '' : 'flag'}
											spritePath={`${i.icon}`}
											title={i?.tippy}
										/>
									) : (
										''
									)}
									{clazzText ? (
										<span className={clazzText}>
											{prefix !== '' ? `${prefix} ` : ''}
											{i.title || i.name}
										</span>
									) : (
										`${prefix !== '' ? `${prefix} ` : ''}${
											i.title || i.name
										}`
									)}
								</button>
							);
						})}
				</Wrapper>
			</div>
		</div>
	);
};

Select.propTypes = {
	options: PropTypes.array,
	currentValue: PropTypes.object,
	size: PropTypes.string,
	color: PropTypes.string,
	setValue: PropTypes.func,
	mainIcon: PropTypes.string,
};

export default Select;
